import {
  legalDocumentError,
  saveLegalPdf,
} from '@chiroup/client-core/functions/legalDocumentClient';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { legalDocumentService } from '../../services/legalDocument.service';

type Props = { clinicId: number };

const ClinicDocumentHistory = ({ clinicId }: Props) => {
  const query = useQuery(
    ['clinicLegalHistory', clinicId],
    () => legalDocumentService.history(clinicId),
    { enabled: Number.isSafeInteger(clinicId) && clinicId > 0 }
  );
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const download = async (versionId: string, acceptanceId?: string) => {
    setError('');
    try {
      await saveLegalPdf(await legalDocumentService.download(versionId, clinicId, acceptanceId));
    } catch (err) {
      setError(legalDocumentError(err));
    }
  };
  const rows =
    query.data?.filter(a =>
      `${a.signerName} ${a.signerEmail} ${a.userId} ${a.title} ${a.version}`
        .toLowerCase()
        .includes(filter.toLowerCase())
    ) || [];
  return (
    <section className="mt-8 text-base sm:text-sm" aria-labelledby="clinic-document-history">
      <h2 id="clinic-document-history" className="text-xl font-semibold">
        Clinic document records
      </h2>
      <p className="mt-2 text-gray-600 dark:text-dark-gray-300">
        Clinic {clinicId}. Each record preserves the document, authenticated account, signing
        statement, and execution copy. Historical records survive membership changes.
      </p>
      <label className="mt-4 block font-medium" htmlFor="document-history-search">
        Find a user or document
        <input
          id="document-history-search"
          name="documentHistorySearch"
          className="mt-2 w-full max-w-lg rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-white"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </label>
      {(error || query.isError) && (
        <p role="alert" className="mt-4 text-red-600">
          {error || 'Records could not be loaded.'}
          <button type="button" className="ml-3 underline" onClick={() => query.refetch()}>
            Retry
          </button>
        </p>
      )}
      {query.isLoading && (
        <p role="status" className="mt-4">
          Loading records…
        </p>
      )}
      {query.isSuccess && !rows.length && (
        <p className="mt-4">
          No versioned records match. Legacy acceptance timestamps do not establish a signature on a
          versioned document.
        </p>
      )}
      <div className="mt-5 divide-y divide-gray-900/10 dark:divide-white/10">
        {rows.map(a => (
          <article key={a.id} className="py-5">
            <h3 className="text-lg font-medium">
              {a.title} · {a.version}
            </h3>
            <p className="mt-2">
              {a.signerName}
              {a.signerTitle ? `, ${a.signerTitle}` : ''} · {a.signerEmail}
            </p>
            <p className="mt-1">
              {new Date(a.acceptedAt).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'long',
              })}{' '}
              · {a.clinicLegalName}
            </p>
            <div className="mt-3 flex flex-wrap gap-4">
              <button
                type="button"
                className="py-2 font-medium text-primary-600 underline dark:text-primary-300"
                onClick={() => download(a.versionId)}
              >
                Original PDF
              </button>
              <button
                type="button"
                className="py-2 font-medium text-primary-600 underline dark:text-primary-300"
                onClick={() => download(a.versionId, a.id)}
              >
                Execution copy
              </button>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer py-2">Evidence details</summary>
              <p className="mt-2 break-all font-mono text-sm">
                Record: {a.id}
                <br />
                User: {a.userId}
                <br />
                Document SHA-256: {a.documentSha256}
                <br />
                Execution SHA-256: {a.receiptSha256}
              </p>
              <p className="mt-3">{a.statement}</p>
            </details>
          </article>
        ))}
      </div>
    </section>
  );
};

export default ClinicDocumentHistory;
